import React from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import './theme.css'
import { AuthProvider, RequireAuth, RequireAdmin } from './auth.jsx'
import { ConfigProvider, RequireFeature } from './config.jsx'
import Layout from './Layout.jsx'
import Login from './pages/Login.jsx'
import Register from './pages/Register.jsx'
import VerifyEmail from './pages/VerifyEmail.jsx'
import AcceptInvite from './pages/AcceptInvite.jsx'
import Projects from './pages/Projects.jsx'
import ProjectWorkspace from './pages/ProjectWorkspace.jsx'
import Billing from './pages/Billing.jsx'
import Organizations from './pages/Organizations.jsx'
import OrganizationDetail from './pages/OrganizationDetail.jsx'
import Templates from './pages/admin/Templates.jsx'
import TemplateDetail from './pages/admin/TemplateDetail.jsx'
import AdminProjects from './pages/admin/Projects.jsx'
import Plans from './pages/admin/Plans.jsx'
import AdminSettings from './pages/admin/Settings.jsx'
import AdminJobs from './pages/admin/Jobs.jsx'
import PresentationMigration from './pages/PresentationMigration.jsx'
import PresentationRun from './pages/PresentationRun.jsx'
import MigrationTemplates from './pages/admin/MigrationTemplates.jsx'
import MigrationJobs from './pages/admin/MigrationJobs.jsx'
import PresentationEditor from './pages/PresentationEditor.jsx'
import PresentationHelp from './pages/PresentationHelp.jsx'
import TemplateTuning from './pages/admin/TemplateTuning.jsx'
import AdminUsers from './pages/admin/Users.jsx'

// Bez tego dowolny błąd renderu (np. nieoczekiwany kształt danych z API) wyciszał całe drzewo Reacta
// do pustego/białego ekranu, bez żadnego komunikatu — zgłoszone jako "biały ekran do czasu odświeżenia".
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null } }
  static getDerivedStateFromError(error) { return { error } }
  componentDidCatch(error, info) { console.error('Błąd renderowania:', error, info) }
  render() {
    if (this.state.error) {
      return (
        <div className="splash">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center', textAlign: 'center', padding: 24, maxWidth: 560 }}>
            <div>Coś poszło nie tak przy wyświetlaniu tej strony.</div>
            <button className="btn btn-primary" onClick={() => window.location.reload()}>Odśwież stronę</button>
            <div className="mono small">{String(this.state.error?.message || this.state.error)}</div>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <ConfigProvider>
          <AuthProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/accept-invite" element={<AcceptInvite />} />
              <Route element={<RequireAuth><Layout /></RequireAuth>}>
                <Route index element={<Projects />} />
                <Route path="migration" element={<PresentationMigration />} />
                <Route path="migration/projects" element={<PresentationMigration />} />
                <Route path="migration/history" element={<PresentationMigration />} />
                <Route path="migration/runs/:id" element={<PresentationRun />} />
                <Route path="presentations/:id/editor" element={<PresentationEditor />} />
                <Route path="help/presentations" element={<PresentationHelp />} />
                <Route path="projects/:id" element={<ProjectWorkspace />} />
                <Route path="billing" element={<RequireFeature flag="billing"><Billing /></RequireFeature>} />
                <Route path="organizations" element={<RequireFeature flag="multi_organization"><Organizations /></RequireFeature>} />
                <Route path="organizations/:id" element={<RequireFeature flag="multi_organization"><OrganizationDetail /></RequireFeature>} />
                <Route path="admin" element={<Navigate to="/admin/templates" replace />} />
                <Route path="admin/templates" element={<RequireAdmin><Templates /></RequireAdmin>} />
                <Route path="admin/templates/:id" element={<RequireAdmin><TemplateDetail /></RequireAdmin>} />
                <Route path="admin/users" element={<RequireAdmin><AdminUsers /></RequireAdmin>} />
                <Route path="admin/projects" element={<RequireAdmin><AdminProjects /></RequireAdmin>} />
                <Route path="admin/billing/plans" element={<RequireAdmin><RequireFeature flag="billing"><Plans /></RequireFeature></RequireAdmin>} />
                <Route path="admin/settings" element={<RequireAdmin><AdminSettings /></RequireAdmin>} />
                <Route path="admin/jobs" element={<RequireAdmin><AdminJobs /></RequireAdmin>} />
                <Route path="admin/migration-templates" element={<RequireAdmin><MigrationTemplates /></RequireAdmin>} />
                <Route path="admin/migration-jobs" element={<RequireAdmin><MigrationJobs /></RequireAdmin>} />
                <Route path="admin/template-tuning/:id" element={<RequireAdmin><TemplateTuning /></RequireAdmin>} />
              </Route>
            </Routes>
          </AuthProvider>
        </ConfigProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>
)
